import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Alert, Card, Spinner, Form } from 'react-bootstrap';
import authService from '../services/authService';
import BackToDashboard from '../components/BackToDashboard';

function TypographySelection() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [fontPairings, setFontPairings] = useState([]);
  const [archetypeInfo, setArchetypeInfo] = useState(null);
  const [selectedPairing, setSelectedPairing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedTypography, setSavedTypography] = useState(null);
  const [fontCategories, setFontCategories] = useState({
    serif: false,
    sansSerif: false,
    handwritten: false,
    display: false,
    monospace: false
  });

  useEffect(() => {
    initializePage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initializePage = async () => {
    setLoading(true);

    const authResult = await authService.checkAuth();
    if (!authResult.authenticated) {
      navigate('/login');
      return;
    }

    // Check if user has saved typography
    await fetchSavedTypography();

    setLoading(false);
  };

  const fetchSavedTypography = async () => {
    try {
      const response = await fetch('/api/brand/get-saved-typography', {
        method: 'GET',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setSavedTypography(data.typography);

        // Display saved typography as a single pairing
        setFontPairings([data.typography]);
        setSelectedPairing(0);

        // Load the fonts
        loadGoogleFonts([data.typography]);

        // Get archetype info
        await fetchArchetypeInfo();
      }
      // If 404, no saved typography - that's okay, user will generate
    } catch (err) {
      console.log('No saved typography found or error fetching:', err);
    }
  };

  const fetchArchetypeInfo = async () => {
    try {
      const response = await fetch('/api/brand/get-archetypes', {
        method: 'GET',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setArchetypeInfo({
          primary: data.primary?.name,
          secondary: data.secondary?.name,
          tertiary: data.tertiary?.name
        });
      }
    } catch (err) {
      console.log('Error fetching archetypes:', err);
    }
  };

  const handleGeneratePairings = async () => {
    setGenerating(true);
    setError('');
    setSavedTypography(null);  // Clear saved state when generating new
    setSelectedPairing(null);   // Clear selection

    // Build array of selected categories
    const selectedCategories = Object.keys(fontCategories)
      .filter(key => fontCategories[key])
      .map(key => {
        // Convert camelCase to readable format
        if (key === 'sansSerif') return 'sans-serif';
        if (key === 'handwritten') return 'handwritten/script';
        return key;
      });

    try {
      const response = await fetch('/api/brand/generate-typography', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          font_categories: selectedCategories
        })
      });

      if (response.ok) {
        const data = await response.json();
        setFontPairings(data.pairings);
        setArchetypeInfo(data.archetype_info);

        // Load Google Fonts dynamically
        loadGoogleFonts(data.pairings);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to generate typography');
      }
    } catch (err) {
      setError('Error generating typography: ' + err.message);
    }

    setGenerating(false);
  };

  const toggleFontCategory = (category) => {
    setFontCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const loadGoogleFonts = (pairings) => {
    // Collect all unique fonts from pairings
    const fonts = new Set();
    pairings.forEach(pairing => {
      if (pairing.heading_font) fonts.add(pairing.heading_font);
      if (pairing.body_font) fonts.add(pairing.body_font);
    });

    // Remove existing font link if any
    const existingLink = document.getElementById('google-fonts-typography');
    if (existingLink) {
      existingLink.remove();
    }

    // Build Google Fonts URL
    // Format: https://fonts.googleapis.com/css2?family=Font+Name:wght@400;700&family=Another+Font:wght@400;700
    const fontFamilies = Array.from(fonts).map(font => {
      const fontName = font.replace(/ /g, '+');
      return `family=${fontName}:wght@400;700`;
    }).join('&');

    const googleFontsUrl = `https://fonts.googleapis.com/css2?${fontFamilies}&display=swap`;

    // Inject Google Fonts link into document head
    const link = document.createElement('link');
    link.id = 'google-fonts-typography';
    link.rel = 'stylesheet';
    link.href = googleFontsUrl;
    document.head.appendChild(link);

    console.log('Loaded Google Fonts:', googleFontsUrl);
  };

  const handleSavePairing = async () => {
    if (selectedPairing === null) return;

    setSaving(true);
    setError('');

    try {
      const pairing = fontPairings[selectedPairing];

      const response = await fetch('/api/brand/save-typography', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ typography: pairing })
      });

      if (response.ok) {
        // Navigate back to Brand Identity dashboard
        navigate('/dashboard/branding');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to save typography');
      }
    } catch (err) {
      setError('Error saving typography: ' + err.message);
    }

    setSaving(false);
  };

  const FontPairingCard = ({ pairing, index }) => {
    const isSelected = selectedPairing === index;

    return (
      <Card
        className={`mb-4 ${isSelected ? 'border-primary border-3' : ''}`}
        style={{ cursor: 'pointer' }}
        onClick={() => setSelectedPairing(index)}
      >
        <Card.Header className={isSelected ? 'bg-primary text-white' : ''}>
          <h5 className="mb-0">
            {isSelected && '✓ '}
            {pairing.name}
          </h5>
        </Card.Header>
        <Card.Body>
          <p className="mb-4">{pairing.description}</p>

          {/* Combined Example - Show this FIRST */}
          <div
            className="mb-4 p-4 rounded"
            style={{
              backgroundColor: '#fffef0',
              border: '2px solid #f0e68c'
            }}
          >
            <small className="text-muted d-block mb-3 text-center"><strong>Preview: How They Look Together</strong></small>
            <div
              style={{
                fontFamily: pairing.heading_font,
                fontSize: '28px',
                fontWeight: pairing.heading_weight || 700,
                marginBottom: '16px',
                color: '#1a1a1a'
              }}
            >
              Transform Your Business
            </div>
            <div
              style={{
                fontFamily: pairing.body_font,
                fontSize: '15px',
                fontWeight: pairing.body_weight || 400,
                lineHeight: 1.7,
                color: '#333'
              }}
            >
              This is how your headings and body text will look together in real content.
              Professional typography creates trust, establishes hierarchy, and makes your content easy to read.
              Notice how the fonts complement each other while maintaining distinct roles.
            </div>
          </div>

          {/* Heading Font Details */}
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <small className="text-muted"><strong>Heading Font:</strong> {pairing.heading_font}</small>
              <small className="text-muted">{pairing.heading_style}</small>
            </div>
            <div
              className="p-2 bg-light rounded mb-1"
              style={{
                fontFamily: pairing.heading_font,
                fontSize: '20px',
                fontWeight: pairing.heading_weight || 700,
                lineHeight: 1.3
              }}
            >
              {pairing.heading_font} — The Quick Brown Fox
            </div>
            <small className="text-muted">
              <strong>Use for:</strong> {pairing.heading_usage}
            </small>
          </div>

          {/* Body Font Details */}
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <small className="text-muted"><strong>Body Font:</strong> {pairing.body_font}</small>
              <small className="text-muted">{pairing.body_style}</small>
            </div>
            <div
              className="p-2 bg-light rounded mb-1"
              style={{
                fontFamily: pairing.body_font,
                fontSize: '14px',
                fontWeight: pairing.body_weight || 400,
                lineHeight: 1.6
              }}
            >
              {pairing.body_font} — The quick brown fox jumps over the lazy dog. Readable body text for paragraphs and descriptions.
            </div>
            <small className="text-muted">
              <strong>Use for:</strong> {pairing.body_usage}
            </small>
          </div>

          {isSelected && (
            <Alert variant="primary" className="mt-3 mb-0">
              <small><strong>✓ Selected</strong> - Click "Save & Continue" to use this pairing</small>
            </Alert>
          )}
        </Card.Body>
      </Card>
    );
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />
        <p className="mt-3">Loading...</p>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <BackToDashboard module="branding" />
      <Row className="justify-content-center">
        <Col md={12} lg={10}>
          <Card className="shadow">
            <Card.Body className="p-5">
              <h1 className="text-center mb-4">✍️ Choose Your Typography</h1>

              {archetypeInfo && (
                <Alert variant="info" className="text-center mb-4">
                  <strong>Your Brand Archetypes:</strong> {archetypeInfo.primary}
                  {archetypeInfo.secondary && ` + ${archetypeInfo.secondary}`}
                  {archetypeInfo.tertiary && ` (with ${archetypeInfo.tertiary} accent)`}
                </Alert>
              )}

              {/* Font Category Preferences */}
              <Card className="mb-4" style={{ backgroundColor: '#f8f9fa' }}>
                <Card.Body>
                  <h6 className="mb-3">Font Style Preferences (Optional)</h6>
                  <p className="text-muted small mb-3">
                    Select font categories to guide the AI. This won't override your archetype, just adds direction.
                  </p>
                  <Row>
                    <Col xs={6} md={4} lg={2} className="mb-2">
                      <Form.Check
                        type="checkbox"
                        id="category-serif"
                        label="Serif"
                        checked={fontCategories.serif}
                        onChange={() => toggleFontCategory('serif')}
                      />
                      <small className="text-muted d-block ms-4">Traditional, elegant</small>
                    </Col>
                    <Col xs={6} md={4} lg={2} className="mb-2">
                      <Form.Check
                        type="checkbox"
                        id="category-sans-serif"
                        label="Sans-serif"
                        checked={fontCategories.sansSerif}
                        onChange={() => toggleFontCategory('sansSerif')}
                      />
                      <small className="text-muted d-block ms-4">Modern, clean</small>
                    </Col>
                    <Col xs={6} md={4} lg={2} className="mb-2">
                      <Form.Check
                        type="checkbox"
                        id="category-handwritten"
                        label="Handwritten"
                        checked={fontCategories.handwritten}
                        onChange={() => toggleFontCategory('handwritten')}
                      />
                      <small className="text-muted d-block ms-4">Personal, artistic</small>
                    </Col>
                    <Col xs={6} md={4} lg={2} className="mb-2">
                      <Form.Check
                        type="checkbox"
                        id="category-display"
                        label="Display"
                        checked={fontCategories.display}
                        onChange={() => toggleFontCategory('display')}
                      />
                      <small className="text-muted d-block ms-4">Bold, unique</small>
                    </Col>
                    <Col xs={6} md={4} lg={2} className="mb-2">
                      <Form.Check
                        type="checkbox"
                        id="category-monospace"
                        label="Monospace"
                        checked={fontCategories.monospace}
                        onChange={() => toggleFontCategory('monospace')}
                      />
                      <small className="text-muted d-block ms-4">Technical, precise</small>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {error && <Alert variant="danger">{error}</Alert>}

              {savedTypography && fontPairings.length === 1 && (
                <Alert variant="success" className="text-center mb-4">
                  <strong>✓ Your Saved Typography</strong> - This is the font pairing you selected. You can regenerate to see new options.
                </Alert>
              )}

              {fontPairings.length === 0 ? (
                <div className="text-center py-5">
                  <p className="lead mb-4">
                    Generate AI-powered font pairings based on your brand archetype.
                  </p>
                  <p className="text-muted mb-4">
                    Claude will analyze your archetype and create 3-4 professional font pairings
                    (heading + body) designed to embody your brand personality.
                  </p>
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={handleGeneratePairings}
                    disabled={generating}
                  >
                    {generating ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Generating Font Pairings...
                      </>
                    ) : (
                      'Generate Typography →'
                    )}
                  </Button>
                </div>
              ) : (
                <>
                  {!savedTypography && (
                    <p className="text-center mb-4">
                      Select your favorite font pairing below. Each pairing includes a heading font and body font designed to work perfectly together.
                    </p>
                  )}

                  {generating && (
                    <Alert variant="info" className="text-center">
                      <Spinner animation="border" size="sm" className="me-2" />
                      <strong>Generating fresh pairings...</strong> Claude is analyzing your brand archetype.
                    </Alert>
                  )}

                  <div style={{ opacity: generating ? 0.5 : 1, transition: 'opacity 0.3s' }}>
                    {fontPairings.map((pairing, index) => (
                      <FontPairingCard key={index} pairing={pairing} index={index} />
                    ))}
                  </div>

                  <div className="d-flex justify-content-between mt-4">
                    <Button
                      variant="outline-secondary"
                      onClick={handleGeneratePairings}
                      disabled={generating}
                    >
                      {generating ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Generating...
                        </>
                      ) : (
                        '🔄 Regenerate Pairings'
                      )}
                    </Button>
                    <Button
                      variant="primary"
                      size="lg"
                      disabled={selectedPairing === null || saving}
                      onClick={handleSavePairing}
                    >
                      {saving ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Saving...
                        </>
                      ) : (
                        'Save & Continue →'
                      )}
                    </Button>
                  </div>
                </>
              )}

              {fontPairings.length === 0 && (
                <div className="text-center mt-4">
                  <Button
                    variant="outline-secondary"
                    onClick={() => navigate('/dashboard/branding')}
                  >
                    ← Back to Dashboard
                  </Button>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default TypographySelection;
